import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash, Plus } from "@phosphor-icons/react";
import { TID } from "@/lib/testIds";
import { iconFor, tintFor } from "@/lib/categoryIcons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CATS = ["flower", "pre-rolls", "vapes", "edibles", "concentrates", "topicals", "accessories"];

export default function AdminProducts() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", brand: "", description: "", category: "flower",
    strain: "hybrid", thc_pct: 22, cbd_pct: 0.5, price: 350,
    image_url: "https://images.pexels.com/photos/20171275/pexels-photo-20171275.jpeg?w=600",
    inventory: 60,
  });

  const load = async () => {
    if (!user?.store_id) return;
    const { data } = await api.get("/products", { params: { store_id: user.store_id } });
    setProducts(data);
  };

  useEffect(() => { load(); }, [user?.store_id]);

  const create = async () => {
    try {
      await api.post("/store/products", form);
      toast.success("Product added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/store/products/${id}`);
    load();
  };

  const updateInventory = async (id, inventory) => {
    await api.patch(`/store/products/${id}`, { inventory });
    load();
  };

  const inputStyle = "bg-white border-2 border-[var(--text)] rounded-full h-11";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">Catalog</span>
          <h1 className="mt-1 font-display text-4xl">Products</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid={TID.storeAddProduct} className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} weight="bold" /> Add product
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-2 border-[var(--text)] text-[var(--text)] max-w-lg rounded-3xl" style={{ boxShadow: "6px 6px 0 0 var(--text)" }}>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">New product</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputStyle} />
              <Input placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputStyle} />
              <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputStyle} />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border-2 border-[var(--text)] rounded-full px-4 py-2 text-sm font-bold h-11">
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select value={form.strain} onChange={(e) => setForm({ ...form, strain: e.target.value })} className="bg-white border-2 border-[var(--text)] rounded-full px-4 py-2 text-sm font-bold h-11">
                  <option value="">— strain —</option>
                  <option value="sativa">sativa</option>
                  <option value="indica">indica</option>
                  <option value="hybrid">hybrid</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" placeholder="THC %" value={form.thc_pct} onChange={(e) => setForm({ ...form, thc_pct: parseFloat(e.target.value) })} className={inputStyle} />
                <Input type="number" placeholder="CBD %" value={form.cbd_pct} onChange={(e) => setForm({ ...form, cbd_pct: parseFloat(e.target.value) })} className={inputStyle} />
                <Input type="number" placeholder="Price (R)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} className={inputStyle} />
              </div>
              <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputStyle} />
              <Input type="number" placeholder="Inventory" value={form.inventory} onChange={(e) => setForm({ ...form, inventory: parseInt(e.target.value) })} className={inputStyle} />
              <button onClick={create} className="btn-primary w-full">Create product</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-white bg-[var(--text)]">
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">THC / CBD</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const { icon: Icon, colour } = iconFor(p.category);
              return (
              <tr key={p.product_id} className="border-b border-[var(--text)]/10">
                <td className="px-4 py-3 flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-md border-2 border-[var(--text)] flex items-center justify-center"
                    style={tintFor(p.category)}
                  >
                    <Icon size={22} weight="regular" color={colour} />
                  </div>
                  <div>
                    <p className="font-black" style={{ fontFamily: "'Fraunces', serif" }}>{p.name}</p>
                    <p className="text-xs text-[var(--text-2)]">{p.brand}{p.strain ? ` · ${p.strain}` : ""}</p>
                  </div>
                </td>
                <td className="px-4 py-3 capitalize text-[var(--text-2)]">{p.category}</td>
                <td className="px-4 py-3 text-[var(--text-2)]">{p.thc_pct}% / {p.cbd_pct}%</td>
                <td className="px-4 py-3 text-right font-display text-lg">R{p.price.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    defaultValue={p.inventory}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v) && v !== p.inventory) updateInventory(p.product_id, v);
                    }}
                    className="w-20 bg-white border-2 border-[var(--text)] rounded-full px-3 py-1 text-right text-sm font-bold"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(p.product_id)} className="text-[var(--text-3)] hover:text-[var(--red)]">
                    <Trash size={16} weight="fill" />
                  </button>
                </td>
              </tr>
              );
            })}
            {products.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-2)] text-sm">
                No products yet. Click &ldquo;Add product&rdquo; to get started.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
